import React, { useCallback, useEffect, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import { useTranslation } from 'react-i18next';

/**
 * Zoom, pan and fullscreen for the contract → milestone graph
 * (MilestoneFlowTimeline). The graph is drawn in a fixed pixel coordinate
 * system (SVG edges + absolutely positioned nodes), so scaling one wrapper
 * scales edges and nodes together. This file owns everything around that
 * canvas: the zoom state, the controls, the scroll/pan viewport and the
 * fullscreen overlay. The timeline decides what the canvas contains.
 */

export const FLOW_ZOOM_MIN = 0.5;
export const FLOW_ZOOM_MAX = 2;
export const FLOW_ZOOM_STEP = 0.25;

export const clampFlowZoom = (zoom: number): number =>
  Math.min(FLOW_ZOOM_MAX, Math.max(FLOW_ZOOM_MIN, Math.round(zoom * 100) / 100));

export interface FlowGraphZoom {
  zoom: number;
  zoomIn: () => void;
  zoomOut: () => void;
  reset: () => void;
  /** Multiply the current zoom (wheel/pinch), clamped to the allowed range. */
  zoomBy: (factor: number) => void;
  setZoom: (zoom: number) => void;
}

export function useFlowGraphZoom(initial = 1): FlowGraphZoom {
  const [zoom, setZoomState] = useState(() => clampFlowZoom(initial));
  const setZoom = useCallback((next: number) => setZoomState(clampFlowZoom(next)), []);
  const zoomIn = useCallback(() => setZoomState(current => clampFlowZoom(current + FLOW_ZOOM_STEP)), []);
  const zoomOut = useCallback(() => setZoomState(current => clampFlowZoom(current - FLOW_ZOOM_STEP)), []);
  const reset = useCallback(() => setZoomState(1), []);
  const zoomBy = useCallback((factor: number) => setZoomState(current => clampFlowZoom(current * factor)), []);
  return { zoom, zoomIn, zoomOut, reset, zoomBy, setZoom };
}

interface FlowGraphZoomControlsProps {
  zoom: FlowGraphZoom;
  /** Present when the graph can be opened in (or closed from) the overlay. */
  onToggleFullscreen?: () => void;
  fullscreen?: boolean;
  /** Hide the − / % / + group (the graph already fits at 100 %); the
   * fullscreen toggle stays. */
  hideZoom?: boolean;
}

/** `− 100 % +` button group, plus an optional fullscreen toggle. */
export function FlowGraphZoomControls({ zoom, onToggleFullscreen, fullscreen = false, hideZoom = false }: FlowGraphZoomControlsProps) {
  const { t } = useTranslation();
  const percent = Math.round(zoom.zoom * 100);
  return (
    <div className="d-flex align-items-center gap-2" data-testid="flow-graph-zoom-controls">
      {!hideZoom && <div className="btn-group btn-group-sm" role="group" aria-label={t('contractTimeline.zoom.groupLabel')} data-testid="flow-graph-zoom-group">
        <button
          type="button"
          className="btn btn-outline-secondary flow-graph-zoom-btn"
          onClick={zoom.zoomOut}
          disabled={zoom.zoom <= FLOW_ZOOM_MIN}
          aria-label={t('contractTimeline.zoom.zoomOut')}
          title={t('contractTimeline.zoom.zoomOut')}
        >
          <i className="feather-zoom-out" aria-hidden="true"></i>
        </button>
        <button
          type="button"
          className="btn btn-outline-secondary flow-graph-zoom-level"
          onClick={zoom.reset}
          aria-label={t('contractTimeline.zoom.reset')}
          title={t('contractTimeline.zoom.reset')}
          data-testid="flow-graph-zoom-level"
        >
          {t('contractTimeline.zoom.level', { percent })}
        </button>
        <button
          type="button"
          className="btn btn-outline-secondary flow-graph-zoom-btn"
          onClick={zoom.zoomIn}
          disabled={zoom.zoom >= FLOW_ZOOM_MAX}
          aria-label={t('contractTimeline.zoom.zoomIn')}
          title={t('contractTimeline.zoom.zoomIn')}
        >
          <i className="feather-zoom-in" aria-hidden="true"></i>
        </button>
      </div>}
      {onToggleFullscreen && (
        <button
          type="button"
          className="btn btn-sm btn-outline-secondary flow-graph-zoom-btn"
          onClick={onToggleFullscreen}
          aria-label={t(fullscreen ? 'contractTimeline.zoom.exitFullscreen' : 'contractTimeline.zoom.fullscreen')}
          title={t(fullscreen ? 'contractTimeline.zoom.exitFullscreen' : 'contractTimeline.zoom.fullscreen')}
          data-testid={fullscreen ? 'flow-graph-exit-fullscreen' : 'flow-graph-fullscreen'}
        >
          <i className={`feather-${fullscreen ? 'minimize-2' : 'maximize-2'}`} aria-hidden="true"></i>
        </button>
      )}
    </div>
  );
}

interface FlowGraphViewportProps {
  /** Unscaled canvas size, in graph pixels. */
  width: number;
  height: number;
  zoom: FlowGraphZoom;
  /** Drag the empty canvas to scroll (fullscreen). */
  pannable?: boolean;
  /** Fill the parent (fullscreen) instead of sizing to the content. */
  fill?: boolean;
  className?: string;
  testId?: string;
  /** Reports whether the unscaled graph fits in the container's width, so the
   * caller can hide zoom when there is nothing to zoom for. */
  onFitsChange?: (fits: boolean) => void;
  /** Selectors tried in order for the node to bring into view whenever the
   * graph becomes visible (first render, or its contract/project is opened). */
  focusSelectors?: string[];
  children: React.ReactNode;
}

/**
 * Scroll container around the scaled canvas. A sizing wrapper takes the
 * scaled size so the scrollbars match what is visible; the canvas keeps its
 * graph-pixel size and is scaled with a transform from the top-left corner.
 * Ctrl/⌘ + wheel and two-finger pinch zoom around the content; drag pans when
 * `pannable`.
 */
export function FlowGraphViewport({ width, height, zoom, pannable = false, fill = false, className, testId, onFitsChange, focusSelectors, children }: FlowGraphViewportProps) {
  const ref = useRef<HTMLDivElement>(null);
  const [panning, setPanning] = useState(false);
  const zoomBy = zoom.zoomBy;
  const focusKey = focusSelectors?.join('|') ?? '';

  // Centre the active node horizontally each time the graph turns visible: a
  // folded contract or project has no width until it is opened. Only this
  // viewport scrolls, never the page, and nothing moves when the graph fits.
  useEffect(() => {
    const element = ref.current;
    if (!element || !focusKey) return;
    let shown = false;
    const centre = () => {
      const visible = element.clientWidth > 0;
      if (!visible) { shown = false; return; }
      if (shown) return;
      shown = true;
      if (element.scrollWidth <= element.clientWidth) return;
      const target = focusKey.split('|').map(selector => element.querySelector<HTMLElement>(selector)).find(Boolean);
      if (!target) return;
      const scale = element.scrollWidth / width;
      const middle = (target.offsetLeft + target.offsetWidth / 2) * scale;
      element.scrollLeft = Math.max(0, middle - element.clientWidth / 2);
    };
    centre();
    const observer = typeof ResizeObserver === 'undefined' ? null : new ResizeObserver(centre);
    observer?.observe(element);
    return () => observer?.disconnect();
  }, [focusKey, width]);

  // Does the whole graph fit at 100 %? Re-measured on window resize and, via
  // ResizeObserver, when only the container changes (sidebar collapse, column
  // layout) and when the graph changes width.
  useEffect(() => {
    const element = ref.current;
    if (!element || !onFitsChange) return;
    const measure = () => onFitsChange(element.clientWidth >= width);
    measure();
    window.addEventListener('resize', measure);
    const observer = typeof ResizeObserver === 'undefined' ? null : new ResizeObserver(measure);
    observer?.observe(element);
    return () => {
      window.removeEventListener('resize', measure);
      observer?.disconnect();
    };
  }, [width, onFitsChange]);

  // Wheel and touch listeners must be non-passive to stop the page from
  // zooming/scrolling itself, which React's synthetic onWheel cannot do.
  useEffect(() => {
    const element = ref.current;
    if (!element) return;
    const onWheel = (event: WheelEvent) => {
      if (!event.ctrlKey && !event.metaKey) return;
      event.preventDefault();
      zoomBy(event.deltaY < 0 ? 1.1 : 1 / 1.1);
    };
    let pinchDistance: number | null = null;
    const distance = (touches: TouchList) =>
      Math.hypot(touches[0].clientX - touches[1].clientX, touches[0].clientY - touches[1].clientY);
    const onTouchStart = (event: TouchEvent) => {
      pinchDistance = event.touches.length === 2 ? distance(event.touches) : null;
    };
    const onTouchMove = (event: TouchEvent) => {
      if (event.touches.length !== 2 || pinchDistance === null) return;
      event.preventDefault();
      const next = distance(event.touches);
      if (pinchDistance > 0) zoomBy(next / pinchDistance);
      pinchDistance = next;
    };
    const onTouchEnd = () => { pinchDistance = null; };
    element.addEventListener('wheel', onWheel, { passive: false });
    element.addEventListener('touchstart', onTouchStart, { passive: true });
    element.addEventListener('touchmove', onTouchMove, { passive: false });
    element.addEventListener('touchend', onTouchEnd);
    element.addEventListener('touchcancel', onTouchEnd);
    return () => {
      element.removeEventListener('wheel', onWheel);
      element.removeEventListener('touchstart', onTouchStart);
      element.removeEventListener('touchmove', onTouchMove);
      element.removeEventListener('touchend', onTouchEnd);
      element.removeEventListener('touchcancel', onTouchEnd);
    };
  }, [zoomBy]);

  // Drag-to-pan on the empty canvas; clicks on nodes keep working because a
  // drag only starts outside buttons.
  const onPointerDown = (event: React.PointerEvent<HTMLDivElement>) => {
    const element = ref.current;
    if (!pannable || !element || event.button !== 0) return;
    if ((event.target as HTMLElement).closest('button, a')) return;
    const start = { x: event.clientX, y: event.clientY, left: element.scrollLeft, top: element.scrollTop };
    const onMove = (move: PointerEvent) => {
      element.scrollLeft = start.left - (move.clientX - start.x);
      element.scrollTop = start.top - (move.clientY - start.y);
    };
    const onUp = () => {
      setPanning(false);
      window.removeEventListener('pointermove', onMove);
      window.removeEventListener('pointerup', onUp);
      window.removeEventListener('pointercancel', onUp);
    };
    setPanning(true);
    window.addEventListener('pointermove', onMove);
    window.addEventListener('pointerup', onUp);
    window.addEventListener('pointercancel', onUp);
    event.preventDefault();
  };

  const classes = [
    'flow-graph-viewport',
    'overflow-auto',
    pannable ? 'flow-graph-viewport--pannable' : '',
    panning ? 'is-panning' : '',
    fill ? 'flow-graph-viewport--fill' : '',
    className ?? ''
  ].filter(Boolean).join(' ');

  return (
    <div ref={ref} className={classes} data-testid={testId} data-zoom={zoom.zoom} onPointerDown={onPointerDown}>
      <div className="flow-graph-sizer" style={{ width: width * zoom.zoom, height: height * zoom.zoom }}>
        <div className="flow-graph-canvas position-relative" style={{ width, height, transform: `scale(${zoom.zoom})` }}>
          {children}
        </div>
      </div>
    </div>
  );
}

interface FlowGraphOverlayProps {
  title: string;
  zoom: FlowGraphZoom;
  onClose: () => void;
  children: React.ReactNode;
}

/** Fullscreen overlay (fixed, covers the app shell) with the zoom controls in
 * a header and the pannable viewport below. Escape closes it unless a modal
 * opened from a node is on top. */
export function FlowGraphOverlay({ title, zoom, onClose, children }: FlowGraphOverlayProps) {
  const { t } = useTranslation();
  const closeRef = useRef<HTMLButtonElement>(null);

  useEffect(() => {
    document.body.classList.add('flow-graph-overlay-open');
    closeRef.current?.focus();
    const onKeyDown = (event: KeyboardEvent) => {
      if (event.key !== 'Escape' || document.querySelector('.modal.d-block')) return;
      onClose();
    };
    document.addEventListener('keydown', onKeyDown);
    return () => {
      document.body.classList.remove('flow-graph-overlay-open');
      document.removeEventListener('keydown', onKeyDown);
    };
  }, [onClose]);

  return createPortal(
    <div className="flow-graph-overlay d-flex flex-column" role="dialog" aria-modal="true" aria-label={title} data-testid="flow-graph-overlay">
      <div className="flow-graph-overlay-header d-flex align-items-center justify-content-between gap-3 px-3 py-2 flex-wrap">
        <div className="flow-graph-overlay-text">
          <div className="fw-semibold text-truncate">{title}</div>
          <div className="small opacity-75">{t('contractTimeline.zoom.overlayHint')}</div>
        </div>
        <div className="d-flex align-items-center gap-2">
          <FlowGraphZoomControls zoom={zoom} />
          <button
            ref={closeRef}
            type="button"
            className="btn btn-sm btn-light flow-graph-zoom-btn"
            onClick={onClose}
            aria-label={t('contractTimeline.zoom.exitFullscreen')}
            data-testid="flow-graph-overlay-close"
          >
            <i className="feather-x me-1" aria-hidden="true"></i>
            {t('common.close')}
          </button>
        </div>
      </div>
      <div className="flow-graph-overlay-body flex-grow-1 d-flex">
        {children}
      </div>
    </div>,
    document.body
  );
}
